import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const [entityCode, setEntityCode] = useState(localStorage.getItem('entity_code') || 'smarten-campus');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await login(entityCode, email, password);
      toast.success('Welcome back!');
      if (Array.isArray(response.user?.permissions) && response.user.permissions.includes('parent.view')) {
        navigate('/my-children');
      } else if (response.user?.role === 'student') {
        navigate('/my-attendance');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0d1720] px-4 py-10">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(217,119,6,0.25),transparent_24%),radial-gradient(circle_at_80%_10%,rgba(45,212,191,0.18),transparent_24%),linear-gradient(180deg,#0d1720_0%,#102433_100%)]" />
      <div className="absolute -left-20 bottom-0 h-72 w-72 rounded-full bg-amber-500/10 blur-3xl" />
      <div className="absolute -right-10 top-10 h-64 w-64 rounded-full bg-teal-400/10 blur-3xl" />

      <div className="relative z-10 grid w-full max-w-5xl overflow-hidden rounded-[2rem] border border-white/10 bg-white/6 shadow-[0_40px_120px_rgba(0,0,0,0.45)] backdrop-blur-2xl lg:grid-cols-[1.1fr_0.9fr]">
        <div className="app-hero flex flex-col justify-between p-10">
          <div>
            <p className="app-kicker">SMARTEN ATTENDANCE</p>
            <h1 className="mt-4 max-w-md text-4xl font-black leading-tight">Operate attendance, access, and movement from one smart control room.</h1>
            <p className="mt-5 max-w-lg text-sm text-white/78">
              Built for schools, colleges, and offices with RFID readers, live movement logs, reports, roles, locations, and audit-ready attendance workflows.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {[
              ['Smart readers', 'Live RFID-driven attendance'],
              ['Rules engine', 'Late, partial, leave, and holiday aware'],
              ['Unified access', 'Web dashboard + mobile admin'],
            ].map(([label, value]) => (
              <div key={label} className="rounded-2xl border border-white/12 bg-white/10 px-4 py-4">
                <p className="text-xs uppercase tracking-[0.22em] text-white/55">{label}</p>
                <p className="mt-2 text-sm font-semibold text-white">{value}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="app-surface-strong p-8 lg:p-10">
        <div className="text-center mb-8">
          <img
            src="/smarten_favicon.svg"
            alt="Smarten Attendance"
            className="mx-auto mb-4 h-20 w-20 rounded-[1.4rem] object-cover shadow-lg shadow-teal-900/10"
          />
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-teal-700/70">Smarten Attendance Access</p>
          <h1 className="mt-3 text-3xl font-black text-slate-900">Welcome back</h1>
          <p className="text-slate-500 text-sm mt-2">Sign in to open your smart attendance workspace</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Entity code</label>
            <input
              type="text"
              value={entityCode}
              onChange={(e) => setEntityCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              className="w-full rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100"
              minLength={2}
              maxLength={63}
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              className="w-full rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100"
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 pr-10 text-sm text-slate-900 shadow-sm outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3.5 text-slate-400 hover:text-slate-700"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200/80 bg-slate-50/85 px-4 py-3 text-sm text-slate-600">
            <p className="font-semibold text-slate-800">Authorized access</p>
            <p className="mt-1">Use your assigned credentials to open the attendance workspace.</p>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-2xl bg-gradient-to-r from-teal-600 via-emerald-500 to-amber-500 py-3 text-white font-semibold shadow-lg shadow-teal-900/15 transition hover:translate-y-[-1px] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        <p className="mt-6 text-center text-xs font-medium uppercase tracking-[0.22em] text-slate-400">
          Powered by <span className="text-slate-600">Induxen India Private Limited</span>
        </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
